import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  GlobalStyles,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Map as MapIcon,
  List as ListIcon,
  Folder as FolderIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { isLawEnforcementPlan } from '../../../utils/subscriptionCategory';
import {
  getEffectiveSweepTeamNumber,
  hasLocalSweepTeamOverride,
  setMySweepTeamNumber,
} from '../../../utils/sweepTeamStorageWeb';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { SweepsFirestore } from '../../../services/sweepsFirestore';
import type { SweepCheckedOffReason, SweepFolder, SweepLocation } from '../../../types/sweep';
import { importSweepLocationsFromCsv, type ImportResult } from '../../../utils/importSweepCsv';
import { importSweepLocationsFromXlsxArrayBuffer } from '../../../utils/importSweepXlsx';
import { downloadSweepsCsv, downloadSweepsXlsx } from '../../../utils/exportSweeps';
import AddSweepLocationDialog from '../sweeps/AddSweepLocationDialog';
import SweepDetailDialog from '../sweeps/SweepDetailDialog';
import SweepCheckOffDialog from '../sweeps/SweepCheckOffDialog';
import {
  formatLocalDateYmd,
  formatSweepScheduledDateLabel,
  normalizeSweepScheduledDateInput,
  sweepIsVisibleInReportSiteBrowse,
} from '../../../utils/sweepSchedule';
import { geocodeAddressNominatim, sleepMs } from '../../../utils/geocodeAddressNominatim';
import {
  formatRerouteTeamLine,
  groupMarkersByTeam,
  nearestNeighborTripOrderIds,
  orderWithFixedEndpoints,
  osrmTripOrderIds,
  sortItemsByStopSequence,
  splitRoutePolylinesByCheckedEdges,
} from '../../../utils/sweepMapRerouteSuggest';
import { fetchRouteLegPolylines, type RoadLegSegment } from '../../../utils/osrmRouteGeometry';
import {
  suggestSweepTeamReassignment,
  teamOverlayFromProposals,
  type TeamMoveProposal,
} from '../../../utils/suggestSweepTeamReassignment';

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DARK_TILES = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';

/**
 * Map "Suggest reroute" (wizard + OSRM trip preview). Flip to `true` to re-enable the toolbar button and flow.
 */
const ENABLE_SWEEP_SUGGEST_REROUTE = false;

type SortKey = 'name' | 'stop' | 'team';

function rowSearchHaystack(r: SweepLocation): string {
  const tn =
    typeof r.teamNumber === 'number' && Number.isFinite(r.teamNumber) ? String(Math.round(r.teamNumber)) : '';
  const parts = [r.name, tn];
  return parts.map((p) => String(p ?? '').toLowerCase()).join(' ');
}

function sortSweepRows(rows: SweepLocation[], sortKey: SortKey): SweepLocation[] {
  const out = [...rows];
  if (sortKey === 'name') {
    out.sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
  } else if (sortKey === 'team') {
    out.sort((a, b) => {
      const ta =
        typeof a.teamNumber === 'number' && Number.isFinite(a.teamNumber) ? Math.round(a.teamNumber) : null;
      const tb =
        typeof b.teamNumber === 'number' && Number.isFinite(b.teamNumber) ? Math.round(b.teamNumber) : null;
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return ta - tb;
    });
  } else {
    out.sort((a, b) => {
      const sa =
        typeof a.stopSequence === 'number' && Number.isFinite(a.stopSequence) ? a.stopSequence : null;
      const sb =
        typeof b.stopSequence === 'number' && Number.isFinite(b.stopSequence) ? b.stopSequence : null;
      if (sa == null && sb == null) return 0;
      if (sa == null) return 1;
      if (sb == null) return -1;
      return sa - sb;
    });
  }
  return out;
}

function reasonLabel(r: SweepCheckedOffReason | null | undefined): string {
  if (!r) return '';
  if (r === 'completed') return 'Completed';
  if (r === 'store_closed') return 'Store closed';
  return 'Other';
}

/** Distinct fill per team; same team id always maps to the same hue. */
function sweepMapFillColorForTeam(item: SweepLocation): string {
  const t =
    typeof item.teamNumber === 'number' && Number.isFinite(item.teamNumber) ? Math.round(item.teamNumber) : null;
  if (t == null) return '#94a3b8';
  const hue = ((t * 137.508) % 360 + 360) % 360;
  return `hsl(${Math.round(hue)}, 72%, 52%)`;
}

/** Hollow ring while stop is open; solid fill once checked off (any reason). */
function sweepMapMarkerPathOptions(item: SweepLocation) {
  const fill = sweepMapFillColorForTeam(item);
  const done = !!item.sweepCheckedOff;
  if (done) {
    return {
      color: '#0f172a',
      weight: 2,
      fillColor: fill,
      fillOpacity: 0.92,
    };
  }
  return {
    color: fill,
    weight: 2.5,
    fillColor: fill,
    fillOpacity: 0,
  };
}

function sweepTeamKeyFromLocation(item: SweepLocation): string {
  const t =
    typeof item.teamNumber === 'number' && Number.isFinite(item.teamNumber) ? Math.round(item.teamNumber) : null;
  return t == null ? '__none__' : `t${t}`;
}

/** Preview-only team reassignment overlay for map grouping / colors. */
function applyTeamOverlay(item: SweepLocation, overlay: Record<string, number> | null): SweepLocation {
  if (!overlay || Object.keys(overlay).length === 0) return item;
  const v = overlay[String(item.id)];
  if (typeof v !== 'number' || !Number.isFinite(v)) return item;
  return { ...item, teamNumber: Math.round(v) };
}

function truncateNotes(s: string, max: number): string {
  const t = String(s || '').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** Stable multiset of map points so check-off / style-only row updates do not retrigger zoom. */
function fitPositionsSignature(positions: [number, number][]): string {
  if (!positions.length) return '';
  const parts = positions.map(([lat, lng]) => `${lat.toFixed(5)},${lng.toFixed(5)}`);
  parts.sort();
  return parts.join('|');
}

function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  const lastFitSig = useRef('');
  useEffect(() => {
    if (!positions.length) {
      lastFitSig.current = '';
      return;
    }
    const sig = fitPositionsSignature(positions);
    if (sig === lastFitSig.current) return;
    lastFitSig.current = sig;
    const b = L.latLngBounds(positions);
    map.fitBounds(b, { padding: [48, 48], maxZoom: 14 });
  }, [map, positions]);
  return null;
}

/** After layout / fullscreen changes, Leaflet must recalc tile sizes. */
function MapInvalidateSize({ tick }: { tick: number }) {
  const map = useMap();
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      map.invalidateSize({ animate: false });
    });
    return () => window.cancelAnimationFrame(id);
  }, [map, tick]);
  return null;
}

function firestoreTimeMs(v: unknown): number {
  if (v == null) return 0;
  if (typeof (v as { toMillis?: () => number }).toMillis === 'function') {
    try {
      return (v as { toMillis: () => number }).toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    try {
      return (v as { toDate: () => Date }).toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof v === 'object' && v !== null && 'seconds' in (v as object)) {
    return (v as { seconds: number }).seconds * 1000;
  }
  if (v instanceof Date) return v.getTime();
  return 0;
}

const sweepPulseIconCache = new Map<string, L.DivIcon>();

function pulseDivIconForTeamColor(teamColor: string): L.DivIcon {
  const c = teamColor.replace(/"/g, '');
  const cacheKey = `glow3|${c}`;
  const hit = sweepPulseIconCache.get(cacheKey);
  if (hit) return hit;
  const icon = L.divIcon({
    className: 'sweep-pulse-marker-root',
    html: `<div class="sweep-pulse-glow-wrap" style="--pulse:${c}"><div class="sweep-map-pulse-dot" style="background:${c};"></div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
  sweepPulseIconCache.set(cacheKey, icon);
  return icon;
}

function hasValidSweepLatLng(r: SweepLocation): boolean {
  const lat = r.latitude;
  const lng = r.longitude;
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng);
}

/** Every stop in this folder is checked off, and the folder has at least one stop (full tenant rows). */
function folderAllStopsCheckedOff(rows: SweepLocation[], folderId: string): boolean {
  const fid = String(folderId || '').trim();
  if (!fid) return false;
  const inFolder = rows.filter((r) => String(r.sweepFolderId ?? '').trim() === fid);
  if (inFolder.length === 0) return false;
  return inFolder.every((r) => !!r.sweepCheckedOff);
}

/** Prefer full `address`; otherwise structured fields for geocoding. */
function buildSweepGeocodeQuery(r: SweepLocation): string | null {
  const a = String(r.address || '').trim();
  if (a) return a;
  const line1 = String(r.addressLine1 || '').trim();
  const city = String(r.city || '').trim();
  const state = String(r.state || '').trim();
  const zip = String(r.zip || '').trim();
  const parts = [line1, city, state, zip].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function isExcelFile(fileName: string, mimeType?: string): boolean {
  const n = String(fileName || '').toLowerCase();
  if (n.endsWith('.xlsx') || n.endsWith('.xls')) return true;
  const m = String(mimeType || '').toLowerCase();
  return (
    m.includes('spreadsheet') ||
    m.includes('excel') ||
    m === 'application/vnd.ms-excel' ||
    m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
}

const SweepsTab: React.FC = () => {
  const { colors } = useTheme();
  const { userProfile } = useAuth();
  const masterOwnerID = String(userProfile?.masterOwnerID || '').trim();
  const ownerID = String(userProfile?.ownerID || '').trim();
  const isAdmin = String(userProfile?.role || '').toLowerCase() === 'admin';
  const isGov = isLawEnforcementPlan(userProfile?.subscriptionPlan);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SweepLocation[]>([]);
  const [folders, setFolders] = useState<SweepFolder[]>([]);
  /** `null` = folder list (root). Otherwise folder Firestore id or `__unfiled__` for sites without a folder. */
  const [folderDrillId, setFolderDrillId] = useState<string | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderSweepDate, setNewFolderSweepDate] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);
  const [manageFoldersOpen, setManageFoldersOpen] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState<string | null>(null);
  const [renameFolderText, setRenameFolderText] = useState('');
  /** YYYY-MM-DD for folder `sweepDate` (calendar sweep day); empty clears. */
  const [renameFolderSweepDate, setRenameFolderSweepDate] = useState('');
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<string>('');
  const [movingToFolder, setMovingToFolder] = useState(false);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [myTeamStr, setMyTeamStr] = useState('');
  const [persistedTeam, setPersistedTeam] = useState<number | null>(null);
  const [filterListByMyTeam, setFilterListByMyTeam] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [mapGeocodingBusy, setMapGeocodingBusy] = useState(false);
  const mapGeocodeAbortRef = useRef(false);
  /** Team keys hidden from the map (`__none__` = no team assigned). */
  const [mapHiddenTeamKeys, setMapHiddenTeamKeys] = useState<Set<string>>(() => new Set());
  /** Map-only polyline order by team (Firestore / stop # unchanged). */
  const [mapReroutePreviewByTeam, setMapReroutePreviewByTeam] = useState<Record<string, string[]> | null>(null);
  const [rerouteDialogOpen, setRerouteDialogOpen] = useState(false);
  const [rerouteDialogBusy, setRerouteDialogBusy] = useState(false);
  const [rerouteDialogLines, setRerouteDialogLines] = useState<string[]>([]);
  const [reroutePendingByTeam, setReroutePendingByTeam] = useState<Record<string, string[]> | null>(null);
  const [rerouteWizardStep, setRerouteWizardStep] = useState<'form' | 'results'>('form');
  const [rerouteFormStartId, setRerouteFormStartId] = useState('');
  const [rerouteFormEndId, setRerouteFormEndId] = useState('');
  const [rerouteSelectedTeamKeys, setRerouteSelectedTeamKeys] = useState<string[]>([]);
  const [rerouteAllowTeamReassign, setRerouteAllowTeamReassign] = useState(false);
  const [rerouteTeamProposals, setRerouteTeamProposals] = useState<TeamMoveProposal[] | null>(null);
  const [mapDisplayTeamById, setMapDisplayTeamById] = useState<Record<string, number> | null>(null);
  const [mapRerouteRoadSegmentsByTeam, setMapRerouteRoadSegmentsByTeam] = useState<Record<string, RoadLegSegment[]> | null>(
    null
  );
  const [mapRerouteGeometryBusy, setMapRerouteGeometryBusy] = useState(false);
  const [mapBaselineRoadSegmentsByTeam, setMapBaselineRoadSegmentsByTeam] = useState<Record<string, RoadLegSegment[]> | null>(
    null
  );
  const [mapBaselineGeometryBusy, setMapBaselineGeometryBusy] = useState(false);
  /** When true, polylines use OSRM road geometry when loaded; when false (default), straight segments between stops. */
  const [mapFollowRoads, setMapFollowRoads] = useState(false);
  const [teamSaveDirty, setTeamSaveDirty] = useState(false);
  const [savingTeamAssignments, setSavingTeamAssignments] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [mapInvalidateTick, setMapInvalidateTick] = useState(0);
  const mapFullscreenSectionRef = useRef<HTMLDivElement | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const [checkOffOpen, setCheckOffOpen] = useState(false);
  const [checkOffId, setCheckOffId] = useState<string | null>(null);
  const [checkOffLabel, setCheckOffLabel] = useState('');
  const [filterBrowseTodayOnly, setFilterBrowseTodayOnly] = useState(false);
  const [bulkScheduleOpen, setBulkScheduleOpen] = useState(false);
  const [bulkScheduleYmd, setBulkScheduleYmd] = useState('');
  const [savingBulkSchedule, setSavingBulkSchedule] = useState(false);
  const [importPreflightOpen, setImportPreflightOpen] = useState(false);
  const [importPreflightMode, setImportPreflightMode] = useState<'new' | 'existing'>('new');
  const [importPreflightExistingFolderId, setImportPreflightExistingFolderId] = useState('');
  const [importPreflightName, setImportPreflightName] = useState('');
  const [importPreflightDateYmd, setImportPreflightDateYmd] = useState('');
  const pendingImportMetaRef = useRef<
    | { mode: 'new'; folderName: string; sweepDateYmd: string }
    | { mode: 'existing'; folderId: string; sweepDateYmd: string }
    | null
  >(null);
  const [folderListSearch, setFolderListSearch] = useState('');

  const loadRows = useCallback(async () => {
    if (!masterOwnerID) return;
    setLoading(true);
    setError('');
    try {
      const [list, fl] = await Promise.all([
        SweepsFirestore.listSweepLocations(masterOwnerID),
        SweepsFirestore.listSweepFolders(masterOwnerID),
      ]);
      setRows(list);
      setFolders(fl);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load sweep locations.');
      setRows([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [masterOwnerID]);

  /** Live list/map updates when a stop is checked off (this tab, another tab, or mobile) without a full page reload. */
  useEffect(() => {
    if (!masterOwnerID || !isGov) return;
    const mo = masterOwnerID.trim();
    if (!mo) return;
    const q = query(collection(db, 'sweepLocations'), where('masterOwnerID', '==', mo));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map(
          (d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) }) as SweepLocation
        );
        setRows(list);
      },
      (e) => {
        setError(e instanceof Error ? e.message : 'Could not sync sweep locations.');
      }
    );
    return () => unsub();
  }, [masterOwnerID, isGov]);

  useEffect(() => {
    if (!masterOwnerID || !isGov) return;
    const team = getEffectiveSweepTeamNumber(masterOwnerID, userProfile?.defaultSweepTeamNumber);
    setPersistedTeam(team);
    setMyTeamStr(team != null ? String(team) : '');
    void loadRows();
  }, [masterOwnerID, isGov, loadRows, userProfile?.defaultSweepTeamNumber]);

  useEffect(() => {
    if (!importPreflightOpen) return;
    setImportPreflightDateYmd((prev) => prev || formatLocalDateYmd());
  }, [importPreflightOpen]);

  useEffect(() => {
    if (folderDrillId == null) {
      mapGeocodeAbortRef.current = true;
      setMapGeocodingBusy(false);
      setViewMode('list');
    }
    setMapReroutePreviewByTeam(null);
    setMapRerouteRoadSegmentsByTeam(null);
    setMapBaselineRoadSegmentsByTeam(null);
    setMapDisplayTeamById(null);
    setTeamSaveDirty(false);
    setRerouteTeamProposals(null);
    setRerouteDialogOpen(false);
    if (document.fullscreenElement && mapFullscreenSectionRef.current === document.fullscreenElement) {
      void document.exitFullscreen?.();
    }
  }, [folderDrillId]);

  useEffect(() => {
    if (viewMode === 'list') {
      setMapReroutePreviewByTeam(null);
      setMapRerouteRoadSegmentsByTeam(null);
      setMapBaselineRoadSegmentsByTeam(null);
      setMapDisplayTeamById(null);
      setTeamSaveDirty(false);
      setRerouteTeamProposals(null);
    }
  }, [viewMode]);

  useEffect(() => {
    const onFs = () => {
      const el = mapFullscreenSectionRef.current;
      const active = !!(el && document.fullscreenElement === el);
      setMapFullscreen(active);
      setMapInvalidateTick((t) => t + 1);
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  useEffect(() => {
    if (viewMode === 'list' && document.fullscreenElement) {
      void document.exitFullscreen?.();
    }
  }, [viewMode]);

  const toggleMapFullscreen = useCallback(async () => {
    const el = mapFullscreenSectionRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch {
      /* user denied or browser blocked */
    }
  }, []);

  const applyPersistedTeamFromInput = useCallback(() => {
    if (!masterOwnerID) return;
    const raw = myTeamStr.trim();
    if (!raw) {
      setMySweepTeamNumber(masterOwnerID, null);
      const eff = getEffectiveSweepTeamNumber(masterOwnerID, userProfile?.defaultSweepTeamNumber);
      setPersistedTeam(eff);
      setMyTeamStr(eff != null ? String(eff) : '');
      return;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    setMySweepTeamNumber(masterOwnerID, Math.round(n));
    setPersistedTeam(Math.round(n));
  }, [masterOwnerID, myTeamStr, userProfile?.defaultSweepTeamNumber]);

  const saveMyTeam = useCallback(() => {
    if (!masterOwnerID) return;
    const raw = myTeamStr.trim();
    if (!raw) {
      setMySweepTeamNumber(masterOwnerID, null);
      const eff = getEffectiveSweepTeamNumber(masterOwnerID, userProfile?.defaultSweepTeamNumber);
      setPersistedTeam(eff);
      setMyTeamStr(eff != null ? String(eff) : '');
      return;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    setMySweepTeamNumber(masterOwnerID, Math.round(n));
    setPersistedTeam(Math.round(n));
  }, [masterOwnerID, myTeamStr, userProfile?.defaultSweepTeamNumber]);

  const displayRows = useMemo(() => {
    if (!filterListByMyTeam || persistedTeam == null) return rows;
    const t = Math.round(persistedTeam);
    return rows.filter((r) => typeof r?.teamNumber === 'number' && Math.round(r.teamNumber) === t);
  }, [rows, filterListByMyTeam, persistedTeam]);

  const browseFilteredRows = useMemo(() => {
    if (!filterBrowseTodayOnly) return displayRows;
    const today = formatLocalDateYmd();
    return displayRows.filter((r) => sweepIsVisibleInReportSiteBrowse(r, today));
  }, [displayRows, filterBrowseTodayOnly]);

  const folderNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of folders) m.set(f.id, f.name);
    return m;
  }, [folders]);

  const activeFolders = useMemo(() => folders.filter((f) => !f.archived), [folders]);
  const archivedFolders = useMemo(() => folders.filter((f) => !!f.archived), [folders]);

  const foldersForGrid = useMemo(() => {
    const q = folderListSearch.trim().toLowerCase();
    if (!q) return activeFolders;
    return activeFolders.filter((f) => String(f.name || '').toLowerCase().includes(q));
  }, [activeFolders, folderListSearch]);

  const archivedFoldersForGrid = useMemo(() => {
    const q = folderListSearch.trim().toLowerCase();
    if (!q) return archivedFolders;
    return archivedFolders.filter((f) => String(f.name || '').toLowerCase().includes(q));
  }, [archivedFolders, folderListSearch]);

  const foldersForAddDialog = useMemo(() => {
    const base = activeFolders;
    const drill = folderDrillId != null && folderDrillId !== '__unfiled__' ? folderDrillId : '';
    if (!drill) return base;
    const extra = folders.find((f) => f.id === drill && f.archived);
    if (!extra || base.some((f) => f.id === drill)) return base;
    return [...base, extra];
  }, [activeFolders, folders, folderDrillId]);

  useEffect(() => {
    if (!importPreflightOpen || importPreflightMode !== 'existing' || activeFolders.length === 0) return;
    const ok = activeFolders.some((f) => f.id === importPreflightExistingFolderId);
    if (ok) return;
    const fid = activeFolders[0]?.id || '';
    setImportPreflightExistingFolderId(fid);
    const f = activeFolders.find((x) => x.id === fid);
    const fd = f?.sweepDate ? normalizeSweepScheduledDateInput(String(f.sweepDate)) : '';
    if (fd) setImportPreflightDateYmd(fd);
  }, [importPreflightOpen, importPreflightMode, activeFolders, importPreflightExistingFolderId]);

  const folderFilteredRows = useMemo(() => {
    if (folderDrillId == null) return [];
    if (folderDrillId === '__unfiled__') {
      return browseFilteredRows.filter((r) => !String(r.sweepFolderId ?? '').trim());
    }
    return browseFilteredRows.filter((r) => String(r.sweepFolderId ?? '').trim() === folderDrillId);
  }, [browseFilteredRows, folderDrillId]);

  const unfiledCount = useMemo(
    () => browseFilteredRows.filter((r) => !String(r.sweepFolderId ?? '').trim()).length,
    [browseFilteredRows]
  );

  const searchedRows = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase();
    if (!q) return folderFilteredRows;
    return folderFilteredRows.filter((r) => rowSearchHaystack(r).includes(q));
  }, [folderFilteredRows, appliedSearch]);

  const listRows = useMemo(() => sortSweepRows(searchedRows, sortKey), [searchedRows, sortKey]);

  const mapMarkers = useMemo(() => {
    return listRows
      .map((item) => {
        if (!hasValidSweepLatLng(item)) return null;
        return { item, lat: item.latitude as number, lng: item.longitude as number };
      })
      .filter(Boolean) as { item: SweepLocation; lat: number; lng: number }[];
  }, [listRows]);

  const teamKeysInMapMarkers = useMemo(() => {
    const s = new Set<string>();
    for (const { item } of mapMarkers) {
      s.add(sweepTeamKeyFromLocation(item));
    }
    return s;
  }, [mapMarkers]);

  useEffect(() => {
    setMapHiddenTeamKeys((prev) => {
      const next = new Set<string>();
      for (const k of Array.from(prev)) {
        if (teamKeysInMapMarkers.has(k)) next.add(k);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [teamKeysInMapMarkers]);

  const filteredMapMarkers = useMemo(
    () => mapMarkers.filter((m) => !mapHiddenTeamKeys.has(sweepTeamKeyFromLocation(m.item))),
    [mapMarkers, mapHiddenTeamKeys]
  );

  const handleMapListToggle = useCallback(() => {
    if (viewMode === 'map') {
      mapGeocodeAbortRef.current = true;
      setMapGeocodingBusy(false);
      setViewMode('list');
      return;
    }
    mapGeocodeAbortRef.current = false;
    const needGeo = listRows.filter((r) => !hasValidSweepLatLng(r) && buildSweepGeocodeQuery(r));
    if (mapMarkers.length > 0 || needGeo.length === 0) {
      setViewMode('map');
      return;
    }
    if (!masterOwnerID) {
      setViewMode('map');
      return;
    }
    setError('');
    setMapGeocodingBusy(true);
    setViewMode('map');
    void (async () => {
      let successCount = 0;
      try {
        for (const row of needGeo) {
          if (mapGeocodeAbortRef.current) break;
          const q = buildSweepGeocodeQuery(row);
          if (!q) continue;
          const coords = await geocodeAddressNominatim(q);
          await sleepMs(1100);
          if (mapGeocodeAbortRef.current || !coords) continue;
          await SweepsFirestore.upsertSweepLocation(
            { masterOwnerID, ownerID: ownerID || undefined, id: row.id },
            { latitude: coords.lat, longitude: coords.lng }
          );
          successCount += 1;
        }
        if (!mapGeocodeAbortRef.current) {
          await loadRows();
          if (successCount === 0 && needGeo.length > 0) {
            setError(
              'Could not resolve any addresses for the map. Check addresses or add latitude and longitude when editing a stop.'
            );
          }
        }
      } catch (e: unknown) {
        if (!mapGeocodeAbortRef.current) {
          setError(e instanceof Error ? e.message : 'Geocoding failed.');
        }
      } finally {
        setMapGeocodingBusy(false);
      }
    })();
  }, [viewMode, listRows, mapMarkers.length, masterOwnerID, ownerID, loadRows]);

  /** Latest check-off per team (by `updatedAt`, then higher stop #). Pulsing ring on map only. */
  const lastCompletedPulseTargets = useMemo(() => {
    const groups = new Map<string, SweepLocation[]>();
    for (const m of filteredMapMarkers) {
      if (!m.item.sweepCheckedOff) continue;
      const key = sweepTeamKeyFromLocation(m.item);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(m.item);
    }
    const out: { id: string; lat: number; lng: number; color: string }[] = [];
    for (const [, items] of Array.from(groups.entries())) {
      const best = items.reduce((a, b) => {
        const ta = firestoreTimeMs(a.updatedAt);
        const tb = firestoreTimeMs(b.updatedAt);
        if (ta !== tb) return ta > tb ? a : b;
        const sa = typeof a.stopSequence === 'number' && Number.isFinite(a.stopSequence) ? a.stopSequence : -1e9;
        const sb = typeof b.stopSequence === 'number' && Number.isFinite(b.stopSequence) ? b.stopSequence : -1e9;
        return sa >= sb ? a : b;
      });
      const lat = best.latitude;
      const lng = best.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }
      out.push({ id: String(best.id), lat, lng, color: sweepMapFillColorForTeam(best) });
    }
    return out;
  }, [filteredMapMarkers]);

  const mapMarkersForRoute = useMemo(
    () =>
      filteredMapMarkers.map((m) => ({
        ...m,
        item: applyTeamOverlay(m.item, mapDisplayTeamById),
      })),
    [filteredMapMarkers, mapDisplayTeamById]
  );

  const mapRerouteSuggestable = useMemo(() => {
    const groups = groupMarkersByTeam(filteredMapMarkers);
    for (const [, items] of Array.from(groups.entries())) {
      const sorted = sortItemsByStopSequence(items).filter((item) => hasValidSweepLatLng(item));
      if (sorted.length >= 2) return true;
    }
    return false;
  }, [filteredMapMarkers]);

  /** Polylines per team: edges between two completed stops are solid; others dashed (map-only reroute order optional). */
  const routePolylinesByTeam = useMemo(() => {
    const groups = groupMarkersByTeam(mapMarkersForRoute);
    const out: {
      segKey: string;
      teamKey: string;
      color: string;
      positions: [number, number][];
      solid: boolean;
      isReroutePreview: boolean;
    }[] = [];
    let segIdx = 0;
    for (const [teamKey, items] of Array.from(groups.entries())) {
      const sorted = sortItemsByStopSequence(items).filter((item) => hasValidSweepLatLng(item));
      if (sorted.length < 2) continue;
      const idSet = new Set(sorted.map((i) => String(i.id)));
      const preview = mapReroutePreviewByTeam?.[teamKey]?.filter((id) => idSet.has(id));
      const idOrder =
        preview && preview.length >= 2 ? preview : sorted.map((i) => String(i.id));
      const idToItem = new Map(sorted.map((i) => [String(i.id), i]));
      const orderedItems = idOrder
        .map((id) => idToItem.get(id))
        .filter((item): item is SweepLocation => !!item && hasValidSweepLatLng(item));
      if (orderedItems.length < 2) continue;
      const isThisTeamPreview = !!(preview && preview.length >= 2);
      const rerouteRoad =
        mapFollowRoads && isThisTeamPreview && mapRerouteRoadSegmentsByTeam?.[teamKey]?.length
          ? mapRerouteRoadSegmentsByTeam[teamKey]
          : null;
      const baselineRoad =
        mapFollowRoads && !isThisTeamPreview && mapBaselineRoadSegmentsByTeam?.[teamKey]?.length
          ? mapBaselineRoadSegmentsByTeam[teamKey]
          : null;
      const roadSegs = rerouteRoad ?? baselineRoad;
      const isReroutePreview = isThisTeamPreview;
      const color = sweepMapFillColorForTeam(sorted[0]);
      if (roadSegs && roadSegs.length) {
        roadSegs.forEach((seg, ri) => {
          if (seg.positions.length < 2) return;
          out.push({
            segKey: `${teamKey}-rd-${ri}-${segIdx++}`,
            teamKey,
            color,
            positions: seg.positions,
            solid: seg.solid,
            isReroutePreview: isThisTeamPreview && !!rerouteRoad,
          });
        });
      } else {
        const segs = splitRoutePolylinesByCheckedEdges(orderedItems, (item) =>
          hasValidSweepLatLng(item) ? [item.latitude as number, item.longitude as number] : null
        );
        for (const seg of segs) {
          if (seg.positions.length < 2) continue;
          out.push({
            segKey: `${teamKey}-${seg.solid ? 's' : 'd'}-${segIdx++}`,
            teamKey,
            color,
            positions: seg.positions,
            solid: seg.solid,
            isReroutePreview,
          });
        }
      }
    }
    return out;
  }, [
    mapMarkersForRoute,
    mapReroutePreviewByTeam,
    mapRerouteRoadSegmentsByTeam,
    mapBaselineRoadSegmentsByTeam,
    mapFollowRoads,
  ]);

  const rerouteGeomAbortRef = useRef(false);
  const baselineGeomAbortRef = useRef(false);
  const baselineGeomTimerRef = useRef<number | null>(null);

  useEffect(() => {
    rerouteGeomAbortRef.current = false;
    if (!mapFollowRoads || !mapReroutePreviewByTeam) {
      setMapRerouteRoadSegmentsByTeam(null);
      setMapRerouteGeometryBusy(false);
      return;
    }
    const idToItem = new Map(listRows.map((r) => [String(r.id), r]));
    setMapRerouteRoadSegmentsByTeam(null);
    setMapRerouteGeometryBusy(true);
    void (async () => {
      const next: Record<string, RoadLegSegment[]> = {};
      try {
        for (const teamKey of Object.keys(mapReroutePreviewByTeam)) {
          if (rerouteGeomAbortRef.current) return;
          const ids = mapReroutePreviewByTeam[teamKey];
          if (!Array.isArray(ids) || ids.length < 2) continue;
          const ordered = ids
            .map((id) => idToItem.get(String(id)))
            .filter((item): item is SweepLocation => !!item && hasValidSweepLatLng(item));
          if (ordered.length < 2) continue;
          const solidPerLeg: boolean[] = [];
          for (let i = 0; i < ordered.length - 1; i++) {
            solidPerLeg.push(!!(ordered[i].sweepCheckedOff && ordered[i + 1].sweepCheckedOff));
          }
          const orderedGeo = ordered.map((l) => ({
            latitude: l.latitude as number,
            longitude: l.longitude as number,
            sweepCheckedOff: l.sweepCheckedOff,
          }));
          const segs = await fetchRouteLegPolylines(orderedGeo, solidPerLeg);
          if (rerouteGeomAbortRef.current) return;
          if (segs && segs.length) next[teamKey] = segs;
        }
        if (!rerouteGeomAbortRef.current) {
          setMapRerouteRoadSegmentsByTeam(Object.keys(next).length ? next : null);
        }
      } finally {
        if (!rerouteGeomAbortRef.current) setMapRerouteGeometryBusy(false);
      }
    })();
    return () => {
      rerouteGeomAbortRef.current = true;
    };
  }, [mapReroutePreviewByTeam, listRows, mapFollowRoads]);

  useEffect(() => {
    baselineGeomAbortRef.current = false;
    if (baselineGeomTimerRef.current != null) {
      window.clearTimeout(baselineGeomTimerRef.current);
      baselineGeomTimerRef.current = null;
    }
    if (
      viewMode !== 'map' ||
      !mapFollowRoads ||
      mapReroutePreviewByTeam != null ||
      mapGeocodingBusy ||
      folderDrillId == null
    ) {
      baselineGeomAbortRef.current = true;
      setMapBaselineRoadSegmentsByTeam(null);
      setMapBaselineGeometryBusy(false);
      return () => {
        baselineGeomAbortRef.current = true;
      };
    }

    baselineGeomTimerRef.current = window.setTimeout(() => {
      baselineGeomTimerRef.current = null;
      baselineGeomAbortRef.current = false;
      void (async () => {
        setMapBaselineGeometryBusy(true);
        setMapBaselineRoadSegmentsByTeam(null);
        const groups = groupMarkersByTeam(mapMarkersForRoute);
        const next: Record<string, RoadLegSegment[]> = {};
        try {
          for (const [teamKey, items] of Array.from(groups.entries())) {
            if (baselineGeomAbortRef.current) return;
            const sorted = sortItemsByStopSequence(items).filter((item) => hasValidSweepLatLng(item));
            if (sorted.length < 2) continue;
            const orderedGeo = sorted.map((l) => ({
              latitude: l.latitude as number,
              longitude: l.longitude as number,
              sweepCheckedOff: l.sweepCheckedOff,
            }));
            const solidPerLeg: boolean[] = [];
            for (let i = 0; i < orderedGeo.length - 1; i++) {
              solidPerLeg.push(!!(orderedGeo[i].sweepCheckedOff && orderedGeo[i + 1].sweepCheckedOff));
            }
            const segs = await fetchRouteLegPolylines(orderedGeo, solidPerLeg);
            if (baselineGeomAbortRef.current) return;
            if (segs && segs.length) next[teamKey] = segs;
          }
          if (!baselineGeomAbortRef.current) {
            setMapBaselineRoadSegmentsByTeam(Object.keys(next).length ? next : null);
          }
        } finally {
          if (!baselineGeomAbortRef.current) setMapBaselineGeometryBusy(false);
        }
      })();
    }, 450);

    return () => {
      baselineGeomAbortRef.current = true;
      if (baselineGeomTimerRef.current != null) {
        window.clearTimeout(baselineGeomTimerRef.current);
        baselineGeomTimerRef.current = null;
      }
    };
  }, [viewMode, mapReroutePreviewByTeam, mapGeocodingBusy, folderDrillId, listRows, mapMarkersForRoute, mapFollowRoads]);

  const labelFromTeamKey = useCallback((key: string) => {
    if (key === '__none__') return 'No team';
    const m = /^t(\d+)$/.exec(key);
    return m ? `Team ${m[1]}` : key;
  }, []);

  const openRerouteWizard = useCallback(() => {
    const defaultTeams = Array.from(
      new Set(filteredMapMarkers.map((m) => sweepTeamKeyFromLocation(m.item)))
    ).sort();
    setRerouteWizardStep('form');
    setRerouteFormStartId('');
    setRerouteFormEndId('');
    setRerouteSelectedTeamKeys(defaultTeams);
    setRerouteAllowTeamReassign(false);
    setRerouteTeamProposals(null);
    setRerouteDialogLines([]);
    setReroutePendingByTeam(null);
    setRerouteDialogOpen(true);
  }, [filteredMapMarkers]);

  const runRerouteSuggestion = useCallback(async () => {
    const startId = rerouteFormStartId.trim();
    const endId = rerouteFormEndId.trim();
    const selected = new Set(rerouteSelectedTeamKeys);
    if (selected.size === 0) {
      setRerouteWizardStep('results');
      setRerouteDialogLines(['Select at least one team to optimize.']);
      setReroutePendingByTeam(null);
      return;
    }
    setRerouteWizardStep('results');
    setRerouteDialogBusy(true);
    setRerouteDialogLines([]);
    setReroutePendingByTeam(null);
    try {
      let proposals: TeamMoveProposal[] | null = null;
      if (rerouteAllowTeamReassign) {
        proposals = suggestSweepTeamReassignment(
          folderFilteredRows.filter((r) => hasValidSweepLatLng(r)),
          undefined
        );
        setRerouteTeamProposals(proposals.length ? proposals : null);
      } else {
        setRerouteTeamProposals(null);
      }
      const overlay =
        rerouteAllowTeamReassign && proposals?.length ? teamOverlayFromProposals(proposals, true) : null;
      const withOverlay = filteredMapMarkers.map((m) => ({
        ...m,
        item: applyTeamOverlay(m.item, overlay),
      }));
      const groups = groupMarkersByTeam(withOverlay);
      const byTeam: Record<string, string[]> = {};
      const lines: string[] = [];
      for (const [teamKey, items] of Array.from(groups.entries())) {
        if (!selected.has(teamKey)) continue;
        let ordered = sortItemsByStopSequence(items).filter((item) => hasValidSweepLatLng(item));
        if (ordered.length < 2) continue;
        const startIn = startId && ordered.some((s) => String(s.id) === startId);
        const endIn = endId && ordered.some((s) => String(s.id) === endId);
        ordered = orderWithFixedEndpoints(ordered, startIn ? startId : null, endIn ? endId : null);
        const payload = ordered.map((i) => ({
          id: String(i.id),
          latitude: i.latitude as number,
          longitude: i.longitude as number,
        }));
        const fixLast = !!(endIn && payload.length >= 2);
        let order = await osrmTripOrderIds(payload, { fixFirst: true, fixLast });
        if (!order || order.length !== payload.length) {
          order = nearestNeighborTripOrderIds(payload, { fixLast });
        }
        byTeam[teamKey] = order;
        const idToItem = new Map(ordered.map((i) => [String(i.id), i]));
        lines.push(formatRerouteTeamLine(labelFromTeamKey(teamKey), order, idToItem));
      }
      if (lines.length === 0) {
        setRerouteDialogLines([
          'No selected team has two or more stops with coordinates, or start/end filters removed all but one stop.',
        ]);
        setReroutePendingByTeam(null);
      } else {
        lines.unshift(
          'OSRM’s public demo may rate-limit; long routes are split automatically. After you confirm on the map, lines follow roads when the Route service succeeds, otherwise straight segments between stops.'
        );
        lines.push('');
        lines.push(
          'Stop numbers in Firestore are unchanged until you save team moves (if any). Map lines use the suggested visit order for preview only.'
        );
        setReroutePendingByTeam(byTeam);
        setRerouteDialogLines(lines);
      }
    } catch (e: unknown) {
      setRerouteDialogLines([e instanceof Error ? e.message : 'Could not build a route suggestion.']);
      setReroutePendingByTeam(null);
    } finally {
      setRerouteDialogBusy(false);
    }
  }, [
    filteredMapMarkers,
    folderFilteredRows,
    rerouteAllowTeamReassign,
    rerouteFormEndId,
    rerouteFormStartId,
    rerouteSelectedTeamKeys,
    labelFromTeamKey,
  ]);

  const saveTeamAssignments = useCallback(async () => {
    if (!masterOwnerID) return;
    const props = rerouteTeamProposals;
    if (!props?.length) return;
    const moves = props.filter((p) => p.accepted && p.fromTeam !== p.toTeam && p.toTeam != null);
    if (!moves.length) return;
    setSavingTeamAssignments(true);
    setError('');
    try {
      for (const p of moves) {
        await SweepsFirestore.upsertSweepLocation(
          { masterOwnerID, ownerID: ownerID || undefined, id: p.id },
          { teamNumber: p.toTeam as number }
        );
      }
      await SweepsFirestore.bulkRenumberAllSweepStopSequences(masterOwnerID);
      await loadRows();
      setMapDisplayTeamById(null);
      setTeamSaveDirty(false);
      setRerouteTeamProposals(null);
      setMapReroutePreviewByTeam(null);
      setMapRerouteRoadSegmentsByTeam(null);
      setMapBaselineRoadSegmentsByTeam(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save team assignments.');
    } finally {
      setSavingTeamAssignments(false);
    }
  }, [masterOwnerID, ownerID, rerouteTeamProposals, loadRows]);

  const rerouteDialogContainerEl = useMemo(() => {
    if (typeof document === 'undefined') return undefined;
    const el = mapFullscreenSectionRef.current;
    if (el && document.fullscreenElement === el) return el;
    return undefined;
  }, [mapFullscreen, rerouteDialogOpen]);

  const mapLegendTeams = useMemo(() => {
    const seen = new Map<string, { key: string; label: string; color: string; sortKey: number }>();
    for (const { item } of mapMarkers) {
      const t =
        typeof item.teamNumber === 'number' && Number.isFinite(item.teamNumber)
          ? Math.round(item.teamNumber)
          : null;
      const key = t == null ? '__none__' : `t${t}`;
      if (seen.has(key)) continue;
      seen.set(key, {
        key,
        label: t == null ? 'No team' : `Team ${t}`,
        color: sweepMapFillColorForTeam(item),
        sortKey: t == null ? 1_000_000 : t,
      });
    }
    return Array.from(seen.values())
      .sort((a, b) => a.sortKey - b.sortKey);
  }, [mapMarkers]);

  const rerouteStopOptions = useMemo(
    () => listRows.filter((r) => hasValidSweepLatLng(r)),
    [listRows]
  );

  const fitPositions = useMemo(
    () => filteredMapMarkers.map((m) => [m.lat, m.lng] as [number, number]),
    [filteredMapMarkers]
  );

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds([]);
  }, []);

  const toggleSelectId = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const selectAllVisible = useCallback(() => {
    const ids = listRows.map((r) => String(r?.id)).filter(Boolean);
    setSelectedIds(ids);
  }, [listRows]);

  const closeManageFolders = useCallback(() => {
    if (savingFolder) return;
    setManageFoldersOpen(false);
    setRenameFolderId(null);
    setRenameFolderText('');
    setRenameFolderSweepDate('');
  }, [savingFolder]);

  const openImportPreflight = useCallback(() => {
    const drill = folderDrillId;
    if (drill != null && drill !== '__unfiled__' && folders.some((f) => f.id === drill)) {
      setImportPreflightMode('existing');
      setImportPreflightExistingFolderId(drill);
      const f = folders.find((x) => x.id === drill);
      const fromFolder = f?.sweepDate ? normalizeSweepScheduledDateInput(String(f.sweepDate)) : '';
      setImportPreflightDateYmd(fromFolder || formatLocalDateYmd());
    } else {
      setImportPreflightMode('new');
      setImportPreflightExistingFolderId('');
      setImportPreflightDateYmd(formatLocalDateYmd());
    }
    setImportPreflightName('');
    setImportPreflightOpen(true);
  }, [folderDrillId, folders]);

  const onImportFiles = async (
    files: File[],
    meta:
      | { mode: 'new'; folderName: string; sweepDateYmd: string }
      | { mode: 'existing'; folderId: string; sweepDateYmd: string }
  ) => {
    if (!masterOwnerID) return;
    const fileList = files.filter(Boolean);
    if (fileList.length === 0) return;
    setImporting(true);
    setError('');
    try {
      const ymd = normalizeSweepScheduledDateInput(meta.sweepDateYmd);
      if (!ymd) throw new Error('Missing or invalid sweep date.');
      let folderId: string;
      if (meta.mode === 'new') {
        const folderName = String(meta.folderName || '').trim();
        if (!folderName) throw new Error('Missing sweep folder name.');
        folderId = await SweepsFirestore.createSweepFolder({
          masterOwnerID,
          ownerID: ownerID || undefined,
          name: folderName,
          sweepDate: ymd,
        });
      } else {
        folderId = String(meta.folderId || '').trim();
        if (!folderId) throw new Error('Pick a folder to import into.');
      }
      let totalImported = 0;
      let totalSkipped = 0;
      const batchIds: string[] = [];
      const perFileFailures: string[] = [];
      for (const file of fileList) {
        try {
          let result: ImportResult;
          if (isExcelFile(file.name, file.type)) {
            const buf = await file.arrayBuffer();
            result = await importSweepLocationsFromXlsxArrayBuffer({
              masterOwnerID,
              ownerID: ownerID || undefined,
              arrayBuffer: buf,
              sweepFolderId: folderId,
              sweepScheduledDate: ymd,
            });
          } else {
            const csvText = await file.text();
            result = await importSweepLocationsFromCsv({
              masterOwnerID,
              ownerID: ownerID || undefined,
              csvText,
              sweepFolderId: folderId,
              sweepScheduledDate: ymd,
            });
          }
          totalImported += result.imported;
          totalSkipped += result.skipped;
          batchIds.push(result.importBatchId);
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : 'Import failed.';
          perFileFailures.push(`${file.name}: ${msg}`);
        }
      }
      await loadRows();
      setFolderDrillId(folderId);
      const batchLine =
        batchIds.length === 1
          ? `Batch: ${batchIds[0]}`
          : `Batch IDs (${batchIds.length}): ${batchIds.join(', ')}`;
      window.alert(
        `Import complete (${fileList.length} file${fileList.length === 1 ? '' : 's'})\nImported/updated: ${totalImported}\nSkipped: ${totalSkipped}\n${batchLine}${
          perFileFailures.length
            ? `\n\nFailed (${perFileFailures.length}):\n${perFileFailures.join('\n')}`
            : ''
        }`
      );
      if (perFileFailures.length) {
        setError(perFileFailures.join('; '));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Import failed.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!isGov) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color={colors.TextPrimary} gutterBottom>
          Sweeps
        </Typography>
        <Typography color={colors.TextSecondary}>Sweep locations are only available for government plans.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".csv,.txt,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={(e) => {
          const picked = e.target.files;
          const meta = pendingImportMetaRef.current;
          pendingImportMetaRef.current = null;
          const fileArr = picked?.length ? Array.from(picked) : [];
          if (fileArr.length === 0) return;
          if (!meta) {
            window.alert('Use Import, set folder options and date, then choose one or more files.');
            return;
          }
          if (meta.mode === 'new' && !meta.folderName?.trim()) {
            window.alert('Enter a folder name for this sweep, or switch to an existing folder.');
            return;
          }
          if (meta.mode === 'existing' && !meta.folderId?.trim()) {
            window.alert('Choose an existing folder to add stops to, or create a new folder.');
            return;
          }
          void onImportFiles(fileArr, meta);
        }}
      />

      <Card
        className="card-4"
        sx={{
          marginBottom: 3,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderRadius: '20px',
        }}
      >
        <CardContent sx={{ py: 3, px: 3 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 2,
            }}
          >
            Sweep locations
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 2,
            }}
          >
            <TextField
              size="small"
              label="Team #"
              placeholder="e.g. 5"
              value={myTeamStr}
              onChange={(e) => setMyTeamStr(e.target.value)}
              onBlur={() => applyPersistedTeamFromInput()}
              sx={{
                width: 120,
                flexShrink: 0,
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                '& input': { color: colors.TextPrimary },
                '& label': { color: colors.TextMuted },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={saveMyTeam}
              sx={{
                borderColor: 'rgba(34,211,238,0.5)',
                color: '#22d3ee',
                borderRadius: '50px',
                flexShrink: 0,
              }}
            >
              Save team
            </Button>
            <FormControlLabel
              sx={{ margin: 0, flexShrink: 0 }}
              control={
                <Switch
                  checked={filterListByMyTeam && persistedTeam != null}
                  onChange={(_, on) => {
                    if (!on) {
                      setFilterListByMyTeam(false);
                      return;
                    }
                    if (persistedTeam == null) {
                      window.alert('Set a team number (or ask your admin for a default team), then save if needed.');
                      return;
                    }
                    setFilterListByMyTeam(true);
                  }}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' } }}
                />
              }
              label={<Typography color={colors.TextSecondary}>Show only this team in list</Typography>}
            />
            <FormControlLabel
              sx={{ margin: 0, flexShrink: 0 }}
              control={
                <Switch
                  checked={filterBrowseTodayOnly}
                  onChange={(_, on) => setFilterBrowseTodayOnly(on)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' } }}
                />
              }
              label={
                <Typography color={colors.TextSecondary}>
                  Show only Report Site browse (today)
                </Typography>
              }
            />
          </Box>
          {(() => {
            const orgDef = userProfile?.defaultSweepTeamNumber;
            const orgNum =
              typeof orgDef === 'number' && Number.isFinite(orgDef) ? Math.round(orgDef) : null;
            const localOnly = hasLocalSweepTeamOverride(masterOwnerID);
            if (orgNum == null && !localOnly) return null;
            return (
              <Typography variant="caption" color={colors.TextMuted} sx={{ display: 'block', mb: 2, maxWidth: 720 }}>
                {orgNum != null ? (
                  <>
                    Organization default team: <strong>{orgNum}</strong>. You can change team anytime; saving stores
                    your choice on this device and overrides the default.{' '}
                  </>
                ) : (
                  <>Saving a team number stores it on this device. </>
                )}
                {localOnly && orgNum != null
                  ? 'This browser is using your saved team, not only the organization default.'
                  : null}
              </Typography>
            );
          })()}

          {persistedTeam != null ? (
            <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 2 }}>
              Active team filter: {persistedTeam}
              {filterListByMyTeam ? ' · list matches team-scoped browse' : ''}
            </Typography>
          ) : null}

          {folderDrillId != null ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setFolderDrillId(null)}
                sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
              >
                ← All sweeps
              </Button>
              <Typography variant="body2" color={colors.TextSecondary}>
                {folderDrillId === '__unfiled__'
                  ? 'Unfiled stops'
                  : folderNameById.get(folderDrillId) || 'Folder'}
              </Typography>
              {folderDrillId !== '__unfiled__' && folders.find((x) => x.id === folderDrillId)?.archived ? (
                <Chip size="small" label="Archived" sx={{ bgcolor: 'rgba(148,163,184,0.2)' }} />
              ) : null}
              {isAdmin && folderDrillId !== '__unfiled__' && folders.find((x) => x.id === folderDrillId)?.archived ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={savingFolder}
                  onClick={() => {
                    void (async () => {
                      try {
                        setSavingFolder(true);
                        await SweepsFirestore.updateSweepFolder(folderDrillId, { archived: false });
                        await loadRows();
                      } catch (err: unknown) {
                        setError(err instanceof Error ? err.message : 'Unarchive failed.');
                      } finally {
                        setSavingFolder(false);
                      }
                    })();
                  }}
                  sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
                >
                  Unarchive
                </Button>
              ) : null}
              {isAdmin && folderDrillId !== '__unfiled__' ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={savingFolder}
                  onClick={() => {
                    const f = folders.find((x) => x.id === folderDrillId);
                    if (!f) return;
                    setRenameFolderId(f.id);
                    setRenameFolderText(f.name);
                    const d = f.sweepDate ? normalizeSweepScheduledDateInput(String(f.sweepDate)) : '';
                    setRenameFolderSweepDate(d || '');
                    setManageFoldersOpen(true);
                  }}
                  sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
                >
                  Edit name & sweep date
                </Button>
              ) : null}
              {isAdmin &&
              folderDrillId !== '__unfiled__' &&
              !folders.find((x) => x.id === folderDrillId)?.archived &&
              folderAllStopsCheckedOff(rows, folderDrillId) ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={savingFolder}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Archive "${folderNameById.get(folderDrillId) || 'this sweep'}"? It leaves the main list until unarchived.`
                      )
                    )
                      return;
                    void (async () => {
                      try {
                        setSavingFolder(true);
                        await SweepsFirestore.updateSweepFolder(folderDrillId, { archived: true });
                        setFolderDrillId(null);
                        await loadRows();
                      } catch (err: unknown) {
                        setError(err instanceof Error ? err.message : 'Archive failed.');
                      } finally {
                        setSavingFolder(false);
                      }
                    })();
                  }}
                  sx={{ borderColor: 'rgba(251,191,36,0.6)', color: '#fbbf24', borderRadius: '50px' }}
                >
                  Archive folder
                </Button>
              ) : null}
            </Box>
          ) : null}

          {folderDrillId == null ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
              <TextField
                size="small"
                label="Search folders"
                value={folderListSearch}
                onChange={(e) => setFolderListSearch(e.target.value)}
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                  '& input': { color: colors.TextPrimary },
                  '& label': { color: colors.TextMuted },
                }}
              />
              {isAdmin ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FolderIcon />}
                    onClick={() => {
                      setNewFolderName('');
                      setNewFolderSweepDate(formatLocalDateYmd());
                      setNewFolderOpen(true);
                    }}
                    sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
                  >
                    New sweep folder
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setRenameFolderId(null);
                      setRenameFolderText('');
                      setRenameFolderSweepDate('');
                      setManageFoldersOpen(true);
                    }}
                    sx={{ borderColor: 'rgba(255,255,255,0.2)', color: colors.TextPrimary, borderRadius: '50px' }}
                  >
                    Manage folders
                  </Button>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => setAddOpen(true)}
                    sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
                  >
                    Add
                  </Button>
                  <Button
                    startIcon={<UploadIcon />}
                    variant="outlined"
                    size="small"
                    disabled={importing}
                    onClick={() => openImportPreflight()}
                    sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
                  >
                    Import
                  </Button>
                </>
              ) : null}
              <Button
                variant="outlined"
                size="small"
                disabled={!masterOwnerID || exporting !== null}
                onClick={() => {
                  void (async () => {
                    try {
                      setExporting('csv');
                      await downloadSweepsCsv(masterOwnerID);
                    } catch (e: unknown) {
                      setError(e instanceof Error ? e.message : 'Export failed.');
                    } finally {
                      setExporting(null);
                    }
                  })();
                }}
                sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
              >
                {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={!masterOwnerID || exporting !== null}
                onClick={() => {
                  void (async () => {
                    try {
                      setExporting('xlsx');
                      await downloadSweepsXlsx(masterOwnerID);
                    } catch (e: unknown) {
                      setError(e instanceof Error ? e.message : 'Export failed.');
                    } finally {
                      setExporting(null);
                    }
                  })();
                }}
                sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
              >
                {exporting === 'xlsx' ? 'Exporting…' : 'Export XLSX'}
              </Button>
            </Box>
          ) : null}

          {folderDrillId != null ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              label="Search name or team #"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setAppliedSearch(searchInput);
              }}
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                '& input': { color: colors.TextPrimary },
                '& label': { color: colors.TextMuted },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => setAppliedSearch(searchInput)}
              sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
            >
              Search
            </Button>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ color: colors.TextMuted }}>Sort</InputLabel>
              <Select
                label="Sort"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                sx={{
                  color: colors.TextPrimary,
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34,211,238,0.35)' },
                }}
              >
                <MenuItem value="name">Name (A–Z)</MenuItem>
                <MenuItem value="stop">Stop # (low → high)</MenuItem>
                <MenuItem value="team">Team # (low → high)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                mapGeocodingBusy && viewMode === 'map' ? (
                  <CircularProgress size={16} sx={{ color: colors.TextPrimary }} />
                ) : viewMode === 'list' ? (
                  <MapIcon />
                ) : (
                  <ListIcon />
                )
              }
              onClick={handleMapListToggle}
              sx={{ borderColor: 'rgba(255,255,255,0.2)', color: colors.TextPrimary, borderRadius: '50px' }}
            >
              {viewMode === 'list' ? 'Map' : mapGeocodingBusy ? 'Resolving…' : 'List'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!masterOwnerID || exporting !== null}
              onClick={() => {
                void (async () => {
                  try {
                    setExporting('csv');
                    await downloadSweepsCsv(masterOwnerID);
                  } catch (e: unknown) {
                    setError(e instanceof Error ? e.message : 'Export failed.');
                  } finally {
                    setExporting(null);
                  }
                })();
              }}
              sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
            >
              {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!masterOwnerID || exporting !== null}
              onClick={() => {
                void (async () => {
                  try {
                    setExporting('xlsx');
                    await downloadSweepsXlsx(masterOwnerID);
                  } catch (e: unknown) {
                    setError(e instanceof Error ? e.message : 'Export failed.');
                  } finally {
                    setExporting(null);
                  }
                })();
              }}
              sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
            >
              {exporting === 'xlsx' ? 'Exporting…' : 'Export XLSX'}
            </Button>
          </Box>
          ) : null}

          {isAdmin &&
          folderDrillId != null &&
          !(folderDrillId !== '__unfiled__' && folders.find((x) => x.id === folderDrillId)?.archived) ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={() => setAddOpen(true)}
                sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
              >
                Add
              </Button>
              <Button
                startIcon={<UploadIcon />}
                variant="outlined"
                size="small"
                disabled={importing}
                onClick={() => openImportPreflight()}
                sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </Box>
          ) : null}

          {folderDrillId != null ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                if (selectionMode) exitSelectionMode();
                else setSelectionMode(true);
              }}
              sx={{ borderColor: 'rgba(255,255,255,0.2)', color: colors.TextPrimary, borderRadius: '50px' }}
            >
              {selectionMode ? 'Cancel selection' : 'Select sites'}
            </Button>
            {selectionMode ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={listRows.length === 0}
                  onClick={selectAllVisible}
                  sx={{ borderRadius: '50px' }}
                >
                  Select all
                </Button>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ color: colors.TextMuted }}>Move to folder</InputLabel>
                  <Select
                    label="Move to folder"
                    value={moveTargetFolderId}
                    onChange={(e) => setMoveTargetFolderId(String(e.target.value))}
                    sx={{
                      color: colors.TextPrimary,
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34,211,238,0.35)' },
                    }}
                  >
                    <MenuItem value="">
                      <em>Unfiled</em>
                    </MenuItem>
                    {activeFolders.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name || f.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={movingToFolder || selectedIds.length === 0}
                  onClick={() => {
                    if (selectedIds.length === 0) return;
                    void (async () => {
                      try {
                        setMovingToFolder(true);
                        const fid = String(moveTargetFolderId || '').trim();
                        await SweepsFirestore.bulkSetSweepFolderId(selectedIds, fid || null);
                        await loadRows();
                        exitSelectionMode();
                      } catch (e: unknown) {
                        setError(e instanceof Error ? e.message : 'Move failed.');
                      } finally {
                        setMovingToFolder(false);
                      }
                    })();
                  }}
                  sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
                >
                  {movingToFolder ? 'Moving…' : 'Apply move'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={savingBulkSchedule || selectedIds.length === 0}
                  onClick={() => {
                    setBulkScheduleYmd(formatLocalDateYmd());
                    setBulkScheduleOpen(true);
                  }}
                  sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee', borderRadius: '50px' }}
                >
                  Set visit date ({selectedIds.length})
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={savingBulkSchedule || selectedIds.length === 0}
                  onClick={() => {
                    if (selectedIds.length === 0) return;
                    if (!window.confirm(`Clear visit date for ${selectedIds.length} site(s)?`)) return;
                    void (async () => {
                      try {
                        setSavingBulkSchedule(true);
                        await SweepsFirestore.bulkSetSweepScheduledDate(selectedIds, null);
                        await loadRows();
                        exitSelectionMode();
                      } catch (e: unknown) {
                        setError(e instanceof Error ? e.message : 'Update failed.');
                      } finally {
                        setSavingBulkSchedule(false);
                      }
                    })();
                  }}
                  sx={{ borderRadius: '50px' }}
                >
                  Clear visit date
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  disabled={deletingBulk || selectedIds.length === 0}
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (selectedIds.length === 0) return;
                    if (!window.confirm(`Delete ${selectedIds.length} sweep location(s)?`)) return;
                    void (async () => {
                      try {
                        setDeletingBulk(true);
                        await SweepsFirestore.bulkDeleteSweepLocations(selectedIds);
                        exitSelectionMode();
                        await loadRows();
                      } catch (e: unknown) {
                        setError(e instanceof Error ? e.message : 'Delete failed.');
                      } finally {
                        setDeletingBulk(false);
                      }
                    })();
                  }}
                  sx={{ borderRadius: '50px' }}
                >
                  Delete ({selectedIds.length})
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  disabled={deletingBulk || rows.length === 0}
                  onClick={() => {
                    if (rows.length === 0) return;
                    if (
                      !window.confirm(
                        `Delete every sweep site? This removes all ${rows.length} location(s) for your organization.`
                      )
                    )
                      return;
                    void (async () => {
                      try {
                        setDeletingBulk(true);
                        await SweepsFirestore.bulkDeleteSweepLocations(rows.map((r) => String(r.id)));
                        exitSelectionMode();
                        setRows([]);
                      } catch (e: unknown) {
                        setError(e instanceof Error ? e.message : 'Delete failed.');
                      } finally {
                        setDeletingBulk(false);
                      }
                    })();
                  }}
                  sx={{ borderRadius: '50px' }}
                >
                  Delete all
                </Button>
              </>
            ) : null}
          </Box>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {loading || deletingBulk ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : folderDrillId == null ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {unfiledCount > 0 ? (
                <Card
                  sx={{
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(34, 211, 238, 0.2)',
                    borderRadius: '16px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setFolderDrillId('__unfiled__')}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} color={colors.TextPrimary}>
                      Unfiled
                    </Typography>
                    <Typography variant="body2" color={colors.TextSecondary}>
                      {unfiledCount} stop{unfiledCount === 1 ? '' : 's'} not in a named sweep folder
                    </Typography>
                  </CardContent>
                </Card>
              ) : null}
              {foldersForGrid.map((f) => {
                const n = browseFilteredRows.filter((r) => String(r.sweepFolderId ?? '').trim() === f.id).length;
                const canArchive = isAdmin && folderAllStopsCheckedOff(rows, f.id);
                return (
                  <Card
                    key={f.id}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(34, 211, 238, 0.2)',
                      borderRadius: '16px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setFolderDrillId(f.id)}
                  >
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={700} color={colors.TextPrimary}>
                            {f.name || 'Sweep'}
                          </Typography>
                          <Typography variant="body2" color={colors.TextSecondary}>
                            {f.sweepDate
                              ? `Sweep date: ${formatSweepScheduledDateLabel(String(f.sweepDate))}`
                              : 'No sweep date set'}
                            {' · '}
                            {n} stop{n === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                        {canArchive ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={savingFolder}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                !window.confirm(
                                  `Archive "${f.name}"? It leaves the main list until you unarchive it (Manage sweep folders or while viewing the sweep).`
                                )
                              )
                                return;
                              void (async () => {
                                try {
                                  setSavingFolder(true);
                                  await SweepsFirestore.updateSweepFolder(f.id, { archived: true });
                                  if (folderDrillId === f.id) setFolderDrillId(null);
                                  await loadRows();
                                } catch (err: unknown) {
                                  setError(err instanceof Error ? err.message : 'Archive failed.');
                                } finally {
                                  setSavingFolder(false);
                                }
                              })();
                            }}
                            sx={{ flexShrink: 0, borderColor: 'rgba(251,191,36,0.6)', color: '#fbbf24' }}
                          >
                            Archive
                          </Button>
                        ) : null}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {archivedFoldersForGrid.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Typography variant="subtitle2" color={colors.TextMuted} fontWeight={600}>
                    Archived sweeps
                  </Typography>
                  {archivedFoldersForGrid.map((f) => {
                    const n = browseFilteredRows.filter((r) => String(r.sweepFolderId ?? '').trim() === f.id).length;
                    return (
                      <Card
                        key={`arch-${f.id}`}
                        sx={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(148, 163, 184, 0.25)',
                          borderRadius: '16px',
                          cursor: 'pointer',
                        }}
                        onClick={() => setFolderDrillId(f.id)}
                      >
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700} color={colors.TextPrimary}>
                                {f.name || 'Sweep'}
                              </Typography>
                              <Typography variant="body2" color={colors.TextSecondary}>
                                {f.sweepDate
                                  ? `Sweep date: ${formatSweepScheduledDateLabel(String(f.sweepDate))}`
                                  : 'No sweep date set'}
                                {' · '}
                                {n} stop{n === 1 ? '' : 's'}
                              </Typography>
                            </Box>
                            <Chip size="small" label="Archived" sx={{ flexShrink: 0, bgcolor: 'rgba(148,163,184,0.2)' }} />
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : null}
              {foldersForGrid.length === 0 && archivedFoldersForGrid.length === 0 && unfiledCount === 0 ? (
                <Typography color={colors.TextMuted} textAlign="center" sx={{ py: 4 }}>
                  {isAdmin
                    ? 'No sweep folders yet. Tap Import to name a sweep, set the date, and load your spreadsheet.'
                    : 'No sweep folders yet. Ask an admin to import sweeps.'}
                </Typography>
              ) : null}
            </Box>
          ) : viewMode === 'map' ? (
            mapGeocodingBusy ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={36} sx={{ color: '#22d3ee', mb: 2 }} />
                <Typography color={colors.TextMuted}>
                  Resolving addresses for the map (OpenStreetMap). This can take a few seconds per stop.
                </Typography>
                <Typography variant="caption" color={colors.TextMuted} display="block" sx={{ mt: 1 }}>
                  Switch to List to cancel.
                </Typography>
              </Box>
            ) : mapMarkers.length === 0 ? (
              <Typography color={colors.TextMuted} textAlign="center" sx={{ py: 4 }}>
                No stops with latitude and longitude in the current list. Add address fields or coordinates when editing
                a stop, or import a file that includes coordinates.
              </Typography>
            ) : (
              <Box
                ref={mapFullscreenSectionRef}
                sx={{
                  ...(mapFullscreen
                    ? {
                        height: '100%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#06060b',
                        py: 1,
                      }
                    : {}),
                }}
              >
                <GlobalStyles
                  styles={`
                    .sweep-pulse-marker-root {
                      background: transparent !important;
                      border: none !important;
                      pointer-events: none;
                    }
                    .sweep-pulse-glow-wrap {
                      width: 32px;
                      height: 32px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      border-radius: 50%;
                      animation: sweepPulseGlow 2.2s ease-in-out infinite;
                    }
                    @keyframes sweepPulseGlow {
                      0%, 100% {
                        filter: drop-shadow(0 0 2px var(--pulse)) drop-shadow(0 0 5px var(--pulse));
                      }
                      50% {
                        filter: drop-shadow(0 0 4px var(--pulse)) drop-shadow(0 0 8px var(--pulse));
                      }
                    }
                    .sweep-map-pulse-dot {
                      width: 12px;
                      height: 12px;
                      border-radius: 50%;
                      border: 2px solid #0f172a;
                      box-sizing: border-box;
                      animation: sweepMapPulseDot 2s ease-in-out infinite;
                    }
                    @keyframes sweepMapPulseDot {
                      0%, 100% { transform: scale(1); opacity: 1; }
                      50% { transform: scale(1.28); opacity: 0.78; }
                    }
                  `}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setMapHiddenTeamKeys(new Set())}
                    sx={{ color: '#22d3ee', textTransform: 'none', minWidth: 'auto' }}
                  >
                    Show all teams
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setMapHiddenTeamKeys(new Set(teamKeysInMapMarkers))}
                    sx={{ color: colors.TextMuted, textTransform: 'none', minWidth: 'auto' }}
                  >
                    Hide all teams
                  </Button>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={mapFollowRoads}
                        onChange={(e) => setMapFollowRoads(e.target.checked)}
                        sx={{ ml: 0.5 }}
                      />
                    }
                    label={
                      <Typography component="span" variant="body2" sx={{ color: colors.TextPrimary, fontSize: '0.8rem' }}>
                        Follow roads
                      </Typography>
                    }
                    sx={{ mr: 0.5, userSelect: 'none' }}
                  />
                  <Button
                    size="small"
                    variant="text"
                    disabled={
                      !ENABLE_SWEEP_SUGGEST_REROUTE || !mapRerouteSuggestable || rerouteDialogBusy
                    }
                    onClick={() => {
                      if (!ENABLE_SWEEP_SUGGEST_REROUTE) return;
                      openRerouteWizard();
                    }}
                    title={
                      ENABLE_SWEEP_SUGGEST_REROUTE
                        ? undefined
                        : 'Suggest reroute is disabled for now. Set ENABLE_SWEEP_SUGGEST_REROUTE to true in SweepsTab.tsx to enable.'
                    }
                    sx={{ color: '#fbbf24', textTransform: 'none', minWidth: 'auto' }}
                  >
                    Suggest reroute
                  </Button>
                  {teamSaveDirty ? (
                    <Button
                      size="small"
                      variant="text"
                      disabled={savingTeamAssignments || !masterOwnerID}
                      onClick={() => void saveTeamAssignments()}
                      sx={{ color: '#4ade80', textTransform: 'none', minWidth: 'auto' }}
                    >
                      {savingTeamAssignments ? 'Saving teams…' : 'Save team assignments'}
                    </Button>
                  ) : null}
                  {mapReroutePreviewByTeam ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setMapReroutePreviewByTeam(null);
                        setMapRerouteRoadSegmentsByTeam(null);
                        setMapBaselineRoadSegmentsByTeam(null);
                        setMapDisplayTeamById(null);
                        setTeamSaveDirty(false);
                        setRerouteTeamProposals(null);
                      }}
                      sx={{ color: colors.TextMuted, textTransform: 'none', minWidth: 'auto' }}
                    >
                      Use original map routes
                    </Button>
                  ) : null}
                  {mapFollowRoads && (mapRerouteGeometryBusy || mapBaselineGeometryBusy) ? (
                    <Typography variant="caption" color={colors.TextMuted}>
                      Loading road geometry…
                    </Typography>
                  ) : null}
                  <Button
                    size="small"
                    variant="text"
                    startIcon={mapFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    onClick={() => void toggleMapFullscreen()}
                    sx={{ color: colors.TextPrimary, textTransform: 'none', minWidth: 'auto' }}
                  >
                    {mapFullscreen ? 'Exit fullscreen' : 'Fullscreen map'}
                  </Button>
                  {mapLegendTeams.map((row) => {
                    const hidden = mapHiddenTeamKeys.has(row.key);
                    return (
                      <Chip
                        key={row.key}
                        clickable
                        size="small"
                        label={row.label}
                        onClick={() =>
                          setMapHiddenTeamKeys((prev) => {
                            const n = new Set(prev);
                            if (n.has(row.key)) n.delete(row.key);
                            else n.add(row.key);
                            return n;
                          })
                        }
                        variant={hidden ? 'outlined' : 'filled'}
                        sx={{
                          cursor: 'pointer',
                          borderColor: row.color,
                          color: row.color,
                          bgcolor: hidden ? 'transparent' : alpha(row.color, 0.22),
                          opacity: hidden ? 0.45 : 1,
                          '&:hover': { opacity: 1 },
                        }}
                      />
                    );
                  })}
                  <Typography variant="caption" color={colors.TextMuted}>
                    Tap a team to show or hide it. Solid lines run between two completed stops; dashed elsewhere.
                    Markers hollow until checked off. Turn on Follow roads for OSRM paths vs straight chords; the public
                    demo may rate-limit. Suggest reroute previews a new visit order; stop numbers change only after you
                    save team moves. Pulse + glow = last completed stop per team (by update time).
                  </Typography>
                </Box>
                {filteredMapMarkers.length === 0 ? (
                  <Typography color={colors.TextMuted} textAlign="center" sx={{ py: 4 }}>
                    All teams are hidden. Tap a team chip above to show it on the map.
                  </Typography>
                ) : (
                <Box
                  sx={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(34,211,238,0.2)',
                    ...(mapFullscreen
                      ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                      : { height: 480 }),
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      flex: mapFullscreen ? 1 : undefined,
                      minHeight: mapFullscreen ? 0 : undefined,
                      height: '100%',
                    }}
                  >
                  <MapContainer
                    center={[filteredMapMarkers[0].lat, filteredMapMarkers[0].lng]}
                    zoom={12}
                    style={{ height: '100%', width: '100%', background: '#0a0a0f' }}
                    attributionControl
                  >
                    <TileLayer url={DARK_TILES} attribution='&copy; <a href="https://carto.com/">CARTO</a>' />
                    <FitBounds positions={fitPositions} />
                    <MapInvalidateSize tick={mapInvalidateTick} />
                    {routePolylinesByTeam.map((pl) => (
                      <Polyline
                        key={pl.segKey}
                        positions={pl.positions}
                        pathOptions={{
                          color: pl.color,
                          weight: pl.isReroutePreview ? 4 : 3,
                          opacity: 0.78,
                          ...(pl.solid ? {} : { dashArray: '8 12' }),
                        }}
                      />
                    ))}
                    {filteredMapMarkers.map(({ item, lat, lng }) => {
                      const displayItem = applyTeamOverlay(item, mapDisplayTeamById);
                      return (
                      <CircleMarker
                        key={`${String(item.id)}-${item.sweepCheckedOff ? '1' : '0'}`}
                        center={[lat, lng]}
                        radius={9}
                        pathOptions={sweepMapMarkerPathOptions(displayItem)}
                      >
                        <Popup>
                          <Box
                            sx={{
                              color: '#0a0a0a',
                              minWidth: 200,
                              '& .MuiTypography-root': { color: '#0a0a0a' },
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              {item.name || 'Unnamed'}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {item.address || '—'}
                            </Typography>
                            {typeof displayItem.teamNumber === 'number' && Number.isFinite(displayItem.teamNumber) ? (
                              <Typography variant="caption" display="block">
                                Team {Math.round(displayItem.teamNumber)}
                                {mapDisplayTeamById &&
                                typeof item.teamNumber === 'number' &&
                                Number.isFinite(item.teamNumber) &&
                                Math.round(item.teamNumber) !== Math.round(displayItem.teamNumber) ? (
                                  <span> (saved: {Math.round(item.teamNumber)})</span>
                                ) : null}
                              </Typography>
                            ) : null}
                            {typeof item.stopSequence === 'number' ? (
                              <Typography variant="caption" display="block">
                                Stop #{item.stopSequence}
                              </Typography>
                            ) : null}
                            {String(item.sweepFolderId ?? '').trim() &&
                            folderNameById.get(String(item.sweepFolderId)) ? (
                              <Typography variant="caption" display="block">
                                Folder: {folderNameById.get(String(item.sweepFolderId))}
                              </Typography>
                            ) : null}
                            {item.sweepCheckedOff ? (
                              <Typography variant="caption" display="block">
                                {reasonLabel(item.sweepCheckedOffReason)}
                                {item.sweepCheckedOffNotes ? ` — ${item.sweepCheckedOffNotes}` : ''}
                              </Typography>
                            ) : null}
                            {String(item.sweepScheduledDate ?? '').trim() ? (
                              <Typography variant="caption" display="block">
                                Visit: {formatSweepScheduledDateLabel(String(item.sweepScheduledDate))}
                              </Typography>
                            ) : (
                              <Typography variant="caption" display="block">
                                Visit date: any day
                              </Typography>
                            )}
                          </Box>
                        </Popup>
                      </CircleMarker>
                      );
                    })}
                    {lastCompletedPulseTargets.map((p) => (
                      <Marker
                        key={`pulse-${p.id}`}
                        position={[p.lat, p.lng]}
                        icon={pulseDivIconForTeamColor(p.color)}
                        zIndexOffset={2000}
                        interactive={false}
                      />
                    ))}
                  </MapContainer>
                  </Box>
                </Box>
                )}
              </Box>
            )
          ) : listRows.length === 0 ? (
            <Typography color={colors.TextMuted} textAlign="center" sx={{ py: 4 }}>
              {filterListByMyTeam && persistedTeam != null
                ? `No sweep locations for team ${persistedTeam}. Import data or turn off the list filter.`
                : appliedSearch.trim()
                  ? 'No sweep locations match your search.'
                  : 'No stops in this folder yet.'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {listRows.map((item) => {
                const id = String(item.id);
                const selected = selectedIds.includes(id);
                const checkedOff = !!item.sweepCheckedOff;
                return (
                  <Card
                    key={id}
                    sx={{
                      background: checkedOff ? 'rgba(255,255,255,0.02)' : 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(34, 211, 238, 0.15)',
                      borderRadius: '16px',
                      cursor: selectionMode ? 'default' : 'pointer',
                      opacity: checkedOff ? 0.85 : 1,
                    }}
                    onClick={() => {
                      if (selectionMode) toggleSelectId(id);
                      else setDetailId(id);
                    }}
                  >
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" gap={1} alignItems="flex-start">
                        {selectionMode ? (
                          <Checkbox
                            checked={selected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleSelectId(id)}
                            sx={{ color: '#22d3ee', p: 0.5 }}
                          />
                        ) : null}
                        <Box flex={1}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color={checkedOff ? colors.TextMuted : colors.TextPrimary}
                          >
                            {item.name || 'Unnamed'}
                          </Typography>
                          <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 0.5 }}>
                            {item.address || '—'}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {checkedOff ? (
                              <Chip size="small" label="Checked off" sx={{ height: 22, fontSize: '0.7rem' }} />
                            ) : null}
                            {checkedOff && item.sweepCheckedOffReason ? (
                              <Chip
                                size="small"
                                label={reasonLabel(item.sweepCheckedOffReason)}
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            ) : null}
                            {checkedOff && item.sweepCheckedOffNotes ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={truncateNotes(item.sweepCheckedOffNotes, 42)}
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            ) : null}
                            {typeof item.stopSequence === 'number' ? (
                              <Chip
                                size="small"
                                label={`Stop #${item.stopSequence}`}
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            ) : null}
                            {typeof item.teamNumber === 'number' ? (
                              <Chip
                                size="small"
                                label={`Team: ${item.teamNumber}`}
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            ) : null}
                            {String(item.sweepFolderId ?? '').trim() &&
                            folderNameById.get(String(item.sweepFolderId)) ? (
                              <Chip
                                size="small"
                                icon={<FolderIcon sx={{ fontSize: '0.95rem !important' }} />}
                                label={folderNameById.get(String(item.sweepFolderId))}
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            ) : null}
                            <Chip
                              size="small"
                              label={`Readers: ${typeof item.readerCount === 'number' ? item.readerCount : '—'}`}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                            {String(item.sweepScheduledDate ?? '').trim() ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Visit: ${formatSweepScheduledDateLabel(String(item.sweepScheduledDate))}`}
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                variant="outlined"
                                label="Visit: any day"
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            )}
                            {!item.sweepCheckedOff &&
                            !sweepIsVisibleInReportSiteBrowse(item, formatLocalDateYmd()) ? (
                              <Chip
                                size="small"
                                label="Hidden from browse today"
                                sx={{ height: 22, fontSize: '0.7rem', color: '#fbbf24' }}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                      {!selectionMode && checkedOff ? (
                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            void (async () => {
                              try {
                                await SweepsFirestore.setSweepLocationCheckedOff(id, false);
                                await loadRows();
                              } catch (err: unknown) {
                                setError(err instanceof Error ? err.message : 'Could not restore.');
                              }
                            })();
                          }}
                        >
                          Restore for Report Site browse
                        </Button>
                      ) : null}
                      {!selectionMode && !checkedOff ? (
                        <Button
                          size="small"
                          color="warning"
                          sx={{ mt: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCheckOffId(id);
                            setCheckOffLabel(String(item.name || 'this site').trim());
                            setCheckOffOpen(true);
                          }}
                        >
                          Check off (hide from browse)
                        </Button>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={importPreflightOpen}
        onClose={() => !importing && setImportPreflightOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ color: colors.TextPrimary, fontWeight: 700 }}>Import sweep</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color={colors.TextSecondary}>
            Create a new sweep folder or add stops to one you already have. Set the visit date for Report Site browse,
            then choose one or more CSV or Excel files (same folder and visit date for all).
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={importPreflightMode}
            onChange={(_, v) => {
              if (!v) return;
              setImportPreflightMode(v);
              if (v === 'existing') {
                const nextId =
                  importPreflightExistingFolderId &&
                  activeFolders.some((f) => f.id === importPreflightExistingFolderId)
                    ? importPreflightExistingFolderId
                    : activeFolders[0]?.id || '';
                setImportPreflightExistingFolderId(nextId);
                const f = activeFolders.find((x) => x.id === nextId);
                const fd = f?.sweepDate ? normalizeSweepScheduledDateInput(String(f.sweepDate)) : '';
                if (fd) setImportPreflightDateYmd(fd);
              }
            }}
            disabled={importing}
            sx={{
              '& .MuiToggleButton-root': {
                color: colors.TextSecondary,
                borderColor: 'rgba(34,211,238,0.35)',
                textTransform: 'none',
              },
              '& .Mui-selected': { color: '#22d3ee !important', bgcolor: 'rgba(34,211,238,0.08)' },
            }}
          >
            <ToggleButton value="new">New folder</ToggleButton>
            <ToggleButton value="existing" disabled={activeFolders.length === 0}>
              Existing folder
            </ToggleButton>
          </ToggleButtonGroup>
          {activeFolders.length === 0 ? (
            <Typography variant="caption" color={colors.TextMuted}>
              No active folders yet — import with a new folder name first, or create a folder from the list.
            </Typography>
          ) : null}
          {importPreflightMode === 'new' ? (
            <TextField
              label="Folder / sweep name"
              value={importPreflightName}
              onChange={(e) => setImportPreflightName(e.target.value)}
              disabled={importing}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                '& input': { color: colors.TextPrimary },
                '& label': { color: colors.TextMuted },
              }}
            />
          ) : (
            <FormControl fullWidth disabled={importing}>
              <InputLabel sx={{ color: colors.TextMuted }}>Folder</InputLabel>
              <Select
                label="Folder"
                value={importPreflightExistingFolderId}
                onChange={(e) => {
                  const id = String(e.target.value);
                  setImportPreflightExistingFolderId(id);
                  const f = activeFolders.find((x) => x.id === id);
                  const fd = f?.sweepDate ? normalizeSweepScheduledDateInput(String(f.sweepDate)) : '';
                  if (fd) setImportPreflightDateYmd(fd);
                }}
                sx={{
                  color: colors.TextPrimary,
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(34,211,238,0.35)' },
                }}
              >
                {activeFolders.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                    {f.sweepDate ? ` · ${formatSweepScheduledDateLabel(String(f.sweepDate))}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            type="date"
            label="Date of sweep"
            value={importPreflightDateYmd}
            onChange={(e) => setImportPreflightDateYmd(e.target.value)}
            disabled={importing}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              '& input': { color: colors.TextPrimary },
              '& label': { color: colors.TextMuted },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportPreflightOpen(false)} disabled={importing} sx={{ color: colors.TextMuted }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={importing}
            sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
            onClick={() => {
              const ymd = normalizeSweepScheduledDateInput(importPreflightDateYmd);
              if (!ymd) {
                window.alert('Pick a valid sweep date (YYYY-MM-DD).');
                return;
              }
              if (importPreflightMode === 'new') {
                const nm = String(importPreflightName || '').trim();
                if (!nm) {
                  window.alert('Enter a folder name for this sweep.');
                  return;
                }
                pendingImportMetaRef.current = { mode: 'new', folderName: nm, sweepDateYmd: ymd };
              } else {
                const fid = String(importPreflightExistingFolderId || '').trim();
                if (!fid) {
                  window.alert('Choose a folder to add stops to.');
                  return;
                }
                pendingImportMetaRef.current = { mode: 'existing', folderId: fid, sweepDateYmd: ymd };
              }
              setImportPreflightOpen(false);
              setImportPreflightName('');
              setImportPreflightDateYmd('');
              setImportPreflightExistingFolderId('');
              setTimeout(() => fileInputRef.current?.click(), 0);
            }}
          >
            Choose file(s)…
          </Button>
        </DialogActions>
      </Dialog>

      <AddSweepLocationDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        masterOwnerID={masterOwnerID}
        ownerID={ownerID || undefined}
        folders={foldersForAddDialog}
        defaultSweepFolderId={
          folderDrillId != null && folderDrillId !== '__unfiled__' ? folderDrillId : null
        }
        editing={null}
        onSaved={() => void loadRows()}
      />

      <SweepDetailDialog
        open={detailId != null}
        sweepId={detailId}
        onClose={() => setDetailId(null)}
        masterOwnerID={masterOwnerID}
        ownerID={ownerID || undefined}
        isAdmin={isAdmin}
        onSweepDeleted={() => void loadRows()}
        onSweepUpdated={() => void loadRows()}
      />

      <SweepCheckOffDialog
        open={checkOffOpen}
        onClose={() => {
          setCheckOffOpen(false);
          setCheckOffId(null);
        }}
        locationLabel={checkOffLabel}
        onConfirm={async (reason, notes) => {
          const id = checkOffId;
          if (!id) return;
          await SweepsFirestore.setSweepLocationCheckedOff(id, true, { reason, notes });
          await loadRows();
        }}
      />

      <Dialog
        open={newFolderOpen}
        onClose={() => !savingFolder && setNewFolderOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ color: colors.TextPrimary, fontWeight: 700 }}>New sweep folder</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color={colors.TextSecondary}>
            Name this sweep (folder) and optional date. Assign sites when adding or importing, or use Move to folder in
            the list.
          </Typography>
          <TextField
            label="Sweep name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            disabled={savingFolder}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              '& input': { color: colors.TextPrimary },
              '& label': { color: colors.TextMuted },
            }}
          />
          <TextField
            type="date"
            label="Date of sweep (optional)"
            value={newFolderSweepDate}
            onChange={(e) => setNewFolderSweepDate(e.target.value)}
            disabled={savingFolder}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              '& input': { color: colors.TextPrimary },
              '& label': { color: colors.TextMuted },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewFolderOpen(false)} disabled={savingFolder} sx={{ color: colors.TextMuted }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const nm = String(newFolderName || '').trim();
              if (!nm) {
                window.alert('Enter a sweep name.');
                return;
              }
              void (async () => {
                try {
                  setSavingFolder(true);
                  const sd = normalizeSweepScheduledDateInput(newFolderSweepDate);
                  await SweepsFirestore.createSweepFolder({
                    masterOwnerID,
                    ownerID: ownerID || undefined,
                    name: nm,
                    sweepDate: sd || null,
                  });
                  setNewFolderOpen(false);
                  setNewFolderName('');
                  setNewFolderSweepDate('');
                  await loadRows();
                } catch (e: unknown) {
                  setError(e instanceof Error ? e.message : 'Could not create folder.');
                } finally {
                  setSavingFolder(false);
                }
              })();
            }}
            disabled={savingFolder}
            variant="outlined"
            sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
          >
            {savingFolder ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manageFoldersOpen}
        onClose={closeManageFolders}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ color: colors.TextPrimary, fontWeight: 700 }}>Manage sweep folders</DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(34,211,238,0.15)' }}>
          <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 2 }}>
            Edit sweep name and calendar sweep date, rename, archive when every stop in the folder is checked off, or
            delete. The sweep date is used for sorting and labels; per-site &quot;visit&quot; dates for Report Site browse
            are still edited on each stop (open the site → Edit). Archived folders move to &quot;Archived sweeps&quot;.
            Deleting a folder unfiles its sites.
          </Typography>
          {renameFolderId ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Sweep name"
                  value={renameFolderText}
                  onChange={(e) => setRenameFolderText(e.target.value)}
                  sx={{
                    flex: 1,
                    minWidth: 160,
                    '& input': { color: colors.TextPrimary },
                    '& label': { color: colors.TextMuted },
                  }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Sweep date (optional)"
                  value={renameFolderSweepDate}
                  onChange={(e) => setRenameFolderSweepDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: 160,
                    '& input': { color: colors.TextPrimary },
                    '& label': { color: colors.TextMuted },
                  }}
                />
              </Box>
              <Typography variant="caption" color={colors.TextMuted}>
                Clear the date field to remove the folder sweep date. Visit dates on individual stops are separate.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  size="small"
                  onClick={() => {
                    void (async () => {
                      if (!renameFolderId) return;
                      const nm = String(renameFolderText || '').trim();
                      if (!nm) return;
                      const dateRaw = String(renameFolderSweepDate || '').trim();
                      const sdNorm = dateRaw ? normalizeSweepScheduledDateInput(dateRaw) : undefined;
                      if (dateRaw && !sdNorm) {
                        window.alert('Sweep date must be a valid calendar day (YYYY-MM-DD), or leave empty to clear.');
                        return;
                      }
                      try {
                        setSavingFolder(true);
                        await SweepsFirestore.updateSweepFolder(renameFolderId, {
                          name: nm,
                          sweepDate: dateRaw && sdNorm ? sdNorm : null,
                        });
                        setRenameFolderId(null);
                        setRenameFolderText('');
                        setRenameFolderSweepDate('');
                        await loadRows();
                      } catch (e: unknown) {
                        setError(e instanceof Error ? e.message : 'Save failed.');
                      } finally {
                        setSavingFolder(false);
                      }
                    })();
                  }}
                  disabled={savingFolder}
                  sx={{ color: '#22d3ee' }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setRenameFolderId(null);
                    setRenameFolderText('');
                    setRenameFolderSweepDate('');
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {folders.length === 0 ? (
              <Typography color={colors.TextMuted}>No folders yet. Use New sweep folder.</Typography>
            ) : (
              <>
                {activeFolders.length > 0 ? (
                  <>
                    <Typography variant="caption" color={colors.TextMuted} sx={{ mt: 0.5 }}>
                      Active
                    </Typography>
                    {activeFolders.map((f) => (
                      <Box
                        key={f.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          py: 1.5,
                          px: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.03)',
                        }}
                      >
                        <Typography color={colors.TextPrimary} fontWeight={600}>
                          {f.name}
                          {f.sweepDate ? ` · ${formatSweepScheduledDateLabel(String(f.sweepDate))}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            onClick={() => {
                              setRenameFolderId(f.id);
                              setRenameFolderText(f.name);
                              const d = f.sweepDate ? normalizeSweepScheduledDateInput(String(f.sweepDate)) : '';
                              setRenameFolderSweepDate(d || '');
                            }}
                          >
                            Edit
                          </Button>
                          {folderAllStopsCheckedOff(rows, f.id) ? (
                            <Button
                              size="small"
                              disabled={savingFolder}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    `Archive folder "${f.name}"? It will leave the active list until unarchived.`
                                  )
                                )
                                  return;
                                void (async () => {
                                  try {
                                    setSavingFolder(true);
                                    await SweepsFirestore.updateSweepFolder(f.id, { archived: true });
                                    if (folderDrillId === f.id) setFolderDrillId(null);
                                    await loadRows();
                                  } catch (e: unknown) {
                                    setError(e instanceof Error ? e.message : 'Archive failed.');
                                  } finally {
                                    setSavingFolder(false);
                                  }
                                })();
                              }}
                              sx={{ color: '#fbbf24' }}
                            >
                              Archive
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            color="error"
                            disabled={savingFolder}
                            onClick={() => {
                              if (!window.confirm(`Delete folder "${f.name}"? Sites in this folder become unfiled.`))
                                return;
                              void (async () => {
                                try {
                                  setSavingFolder(true);
                                  await SweepsFirestore.deleteSweepFolder(f.id, masterOwnerID);
                                  if (folderDrillId === f.id) setFolderDrillId(null);
                                  await loadRows();
                                } catch (e: unknown) {
                                  setError(e instanceof Error ? e.message : 'Delete failed.');
                                } finally {
                                  setSavingFolder(false);
                                }
                              })();
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </>
                ) : null}
                {archivedFolders.length > 0 ? (
                  <>
                    <Typography variant="caption" color={colors.TextMuted} sx={{ mt: 1 }}>
                      Archived
                    </Typography>
                    {archivedFolders.map((f) => (
                      <Box
                        key={`mf-arch-${f.id}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          py: 1.5,
                          px: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(148,163,184,0.08)',
                        }}
                      >
                        <Typography color={colors.TextPrimary} fontWeight={600}>
                          {f.name}
                          {f.sweepDate ? ` · ${formatSweepScheduledDateLabel(String(f.sweepDate))}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            disabled={savingFolder}
                            onClick={() => {
                              void (async () => {
                                try {
                                  setSavingFolder(true);
                                  await SweepsFirestore.updateSweepFolder(f.id, { archived: false });
                                  await loadRows();
                                } catch (e: unknown) {
                                  setError(e instanceof Error ? e.message : 'Unarchive failed.');
                                } finally {
                                  setSavingFolder(false);
                                }
                              })();
                            }}
                            sx={{ color: '#22d3ee' }}
                          >
                            Unarchive
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setRenameFolderId(f.id);
                              setRenameFolderText(f.name);
                              const d = f.sweepDate ? normalizeSweepScheduledDateInput(String(f.sweepDate)) : '';
                              setRenameFolderSweepDate(d || '');
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={savingFolder}
                            onClick={() => {
                              if (!window.confirm(`Delete folder "${f.name}"? Sites in this folder become unfiled.`))
                                return;
                              void (async () => {
                                try {
                                  setSavingFolder(true);
                                  await SweepsFirestore.deleteSweepFolder(f.id, masterOwnerID);
                                  if (folderDrillId === f.id) setFolderDrillId(null);
                                  await loadRows();
                                } catch (e: unknown) {
                                  setError(e instanceof Error ? e.message : 'Delete failed.');
                                } finally {
                                  setSavingFolder(false);
                                }
                              })();
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </>
                ) : null}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeManageFolders}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkScheduleOpen}
        onClose={() => !savingBulkSchedule && setBulkScheduleOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ color: colors.TextPrimary, fontWeight: 700 }}>Set visit date</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color={colors.TextSecondary}>
            Applies to {selectedIds.length} selected site(s). They will only appear in Report Site browse on this
            calendar day (local time). Use Clear visit date on the list to return to any day.
          </Typography>
          <TextField
            type="date"
            label="Visit date"
            value={bulkScheduleYmd}
            onChange={(e) => setBulkScheduleYmd(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={savingBulkSchedule}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '12px' },
              '& input': { color: colors.TextPrimary },
              '& label': { color: colors.TextMuted },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkScheduleOpen(false)} disabled={savingBulkSchedule} sx={{ color: colors.TextMuted }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const norm = normalizeSweepScheduledDateInput(bulkScheduleYmd);
              if (!norm) {
                window.alert('Pick a valid date (YYYY-MM-DD).');
                return;
              }
              void (async () => {
                try {
                  setSavingBulkSchedule(true);
                  await SweepsFirestore.bulkSetSweepScheduledDate(selectedIds, norm);
                  setBulkScheduleOpen(false);
                  await loadRows();
                  exitSelectionMode();
                } catch (e: unknown) {
                  setError(e instanceof Error ? e.message : 'Update failed.');
                } finally {
                  setSavingBulkSchedule(false);
                }
              })();
            }}
            disabled={savingBulkSchedule}
            variant="outlined"
            sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
          >
            {savingBulkSchedule ? <CircularProgress size={18} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rerouteDialogOpen}
        onClose={() => {
          if (rerouteDialogBusy) return;
          setRerouteDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        container={rerouteDialogContainerEl}
        disableScrollLock
        PaperProps={{ sx: { borderRadius: '16px', maxHeight: 'min(92vh, 720px)' } }}
      >
        <DialogTitle sx={{ color: colors.TextPrimary, fontWeight: 700 }}>
          {rerouteWizardStep === 'form' ? 'Suggest reroute' : 'Suggested map reroute'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rerouteWizardStep === 'form' ? (
            <>
              <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
                Uses OSRM’s public trip service (fair use; may rate-limit). Optional start/end fix the first and last
                visit for each selected team when those stops appear in that team’s list; middle stops are optimized.
                After you confirm on the map, route geometry follows roads when OSRM Route succeeds; otherwise straight
                chords. Team changes are not saved to Firestore until you tap Save team assignments.
              </Alert>
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: colors.TextMuted }}>Optional start stop</InputLabel>
                <Select
                  label="Optional start stop"
                  value={rerouteFormStartId || ''}
                  onChange={(e) => setRerouteFormStartId(String(e.target.value))}
                  sx={{ color: colors.TextPrimary, borderRadius: '12px' }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {rerouteStopOptions.map((r) => (
                    <MenuItem key={`st-${r.id}`} value={String(r.id)}>
                      {typeof r.stopSequence === 'number' ? `#${r.stopSequence} · ` : ''}
                      {r.name || String(r.id).slice(0, 8)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: colors.TextMuted }}>Optional end stop</InputLabel>
                <Select
                  label="Optional end stop"
                  value={rerouteFormEndId || ''}
                  onChange={(e) => setRerouteFormEndId(String(e.target.value))}
                  sx={{ color: colors.TextPrimary, borderRadius: '12px' }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {rerouteStopOptions.map((r) => (
                    <MenuItem key={`en-${r.id}`} value={String(r.id)}>
                      {typeof r.stopSequence === 'number' ? `#${r.stopSequence} · ` : ''}
                      {r.name || String(r.id).slice(0, 8)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Typography variant="subtitle2" color={colors.TextPrimary} sx={{ mb: 1 }}>
                  Teams to optimize (default = visible on map)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflow: 'auto' }}>
                  {mapLegendTeams
                    .filter((row) => teamKeysInMapMarkers.has(row.key))
                    .map((row) => (
                      <FormControlLabel
                        key={row.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={rerouteSelectedTeamKeys.includes(row.key)}
                            onChange={() => {
                              setRerouteSelectedTeamKeys((prev) =>
                                prev.includes(row.key) ? prev.filter((k) => k !== row.key) : [...prev, row.key]
                              );
                            }}
                            sx={{ color: row.color, py: 0 }}
                          />
                        }
                        label={<Typography variant="body2">{row.label}</Typography>}
                      />
                    ))}
                </Box>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={rerouteAllowTeamReassign}
                    onChange={(e) => setRerouteAllowTeamReassign(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color={colors.TextSecondary}>
                    Allow suggesting different team assignments when it may reduce overlap (review + save required)
                  </Typography>
                }
              />
            </>
          ) : rerouteDialogBusy ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={28} sx={{ color: '#fbbf24' }} />
              <Typography variant="body2" color={colors.TextSecondary}>
                Computing a suggested visit order…
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {rerouteDialogLines.map((line, i) =>
                  line === '' ? (
                    <Box key={`sp-${i}`} sx={{ height: 10 }} />
                  ) : (
                    <Typography key={`ln-${i}`} variant="body2" color={colors.TextPrimary} sx={{ lineHeight: 1.55 }}>
                      {line}
                    </Typography>
                  )
                )}
              </Box>
              {rerouteTeamProposals && rerouteTeamProposals.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" color={colors.TextPrimary} sx={{ mb: 0.5 }}>
                    Proposed team moves
                  </Typography>
                  <Typography variant="caption" color={colors.TextMuted} display="block" sx={{ mb: 1 }}>
                    Uncheck a row to keep the saved team for that stop. Saving writes teams and renumbers stop # within
                    each folder + team.
                  </Typography>
                  <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1 }}>
                    {rerouteTeamProposals.map((p) => (
                      <Box
                        key={p.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          borderBottom: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={p.accepted}
                          onChange={() =>
                            setRerouteTeamProposals((prev) =>
                              prev?.map((x) => (x.id === p.id ? { ...x, accepted: !x.accepted } : x)) ?? null
                            )
                          }
                        />
                        <Typography variant="body2" color={colors.TextPrimary} sx={{ flex: 1 }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color={colors.TextMuted}>
                          {p.fromTeam == null ? '—' : `Team ${p.fromTeam}`} → {p.toTeam == null ? '—' : `Team ${p.toTeam}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          {rerouteWizardStep === 'form' ? (
            <>
              <Button
                onClick={() => {
                  setRerouteDialogOpen(false);
                  setReroutePendingByTeam(null);
                }}
                disabled={rerouteDialogBusy}
                sx={{ color: colors.TextMuted }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void runRerouteSuggestion()}
                disabled={rerouteDialogBusy || rerouteSelectedTeamKeys.length === 0}
                variant="outlined"
                sx={{ borderColor: '#fbbf24', color: '#fbbf24' }}
              >
                Run suggestion
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setRerouteWizardStep('form');
                  setRerouteDialogLines([]);
                  setReroutePendingByTeam(null);
                }}
                disabled={rerouteDialogBusy}
                sx={{ color: colors.TextMuted }}
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  setRerouteDialogOpen(false);
                  setReroutePendingByTeam(null);
                }}
                disabled={rerouteDialogBusy}
                sx={{ color: colors.TextMuted }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (reroutePendingByTeam && Object.keys(reroutePendingByTeam).length > 0) {
                    setMapReroutePreviewByTeam({ ...reroutePendingByTeam });
                    const o =
                      rerouteTeamProposals && rerouteTeamProposals.length
                        ? teamOverlayFromProposals(rerouteTeamProposals, true)
                        : {};
                    setMapDisplayTeamById(Object.keys(o).length ? o : null);
                    setTeamSaveDirty(
                      !!rerouteTeamProposals?.some(
                        (p) => p.accepted && p.fromTeam !== p.toTeam && p.toTeam != null
                      )
                    );
                  }
                  setRerouteDialogOpen(false);
                  setReroutePendingByTeam(null);
                }}
                disabled={rerouteDialogBusy || !reroutePendingByTeam || Object.keys(reroutePendingByTeam).length === 0}
                variant="outlined"
                sx={{ borderColor: '#fbbf24', color: '#fbbf24' }}
              >
                Confirm on map
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SweepsTab;
